import { expressGetFileAsBlobFromRequest } from 'lib/api/express/files';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import { getFileAsBlobFromRequest } from '../../api/files';
import { canPreviewFile } from './index';

interface UseDocumentPreviewProps {
  requestId: string;
  requestType: string;
}

interface UseDocumentPreviewReturn {
  previewOpen: boolean;
  previewLoading: boolean;
  previewError: string | null;
  currentPreviewFile: string | null;
  blobUrl: string | null;
  contentType: string | null;

  handlePreview: (fileName: string) => Promise<void>;
  handleClosePreview: () => void;
  setPreviewError: (error: string | null) => void;
  canPreviewFileName: (fileName: string) => boolean;
}

export const useDocumentPreview = ({
  requestId,
  requestType,
}: UseDocumentPreviewProps): UseDocumentPreviewReturn => {
  const dispatch = useDispatch();

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [currentPreviewFile, setCurrentPreviewFile] = useState<string | null>(
    null,
  );
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [contentType, setContentType] = useState<string | null>(null);
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);

  const handlePreview = async (fileName: string) => {
    setCurrentPreviewFile(fileName);
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewError(null);

    try {
      const data = {
        requestId,
        fileName,
        requestType,
      };

      let fileBlob: { blob: Blob; contentType: string } | null;
      if (user?.clientId) {
        fileBlob = await expressGetFileAsBlobFromRequest(
          dispatch,
          data,
          user?.clientId,
        );
      } else {
        fileBlob = await getFileAsBlobFromRequest(dispatch, data);
      }
      if (!fileBlob) {
        throw new Error('No se pudo obtener el archivo');
      }

      const newBlobUrl = URL.createObjectURL(fileBlob.blob);

      setBlobUrl(newBlobUrl);
      setContentType(fileBlob.contentType);
    } catch (error) {
      console.error('Error al cargar archivo para previsualización:', error);
      setPreviewError(
        'Error al cargar el archivo para previsualización. El servidor puede no soportar esta funcionalidad aún. Intenta descargarlo.',
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
    }
    setBlobUrl(null);
    setPreviewError(null);
    setCurrentPreviewFile(null);
  };

  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  const canPreviewFileName = (fileName: string): boolean => {
    return canPreviewFile('', fileName);
  };

  return {
    previewOpen,
    previewLoading,
    previewError,
    currentPreviewFile,
    blobUrl,
    contentType,

    handlePreview,
    handleClosePreview,
    setPreviewError,
    canPreviewFileName,
  };
};
